import { CallStatus } from '@compass/client/types/call';
import { assetsService } from '@services/assets.service';
import { useStore } from '@store/store';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import type { OrgProgramContextOverride } from '@/types/context';
import {
  mergeTranscriptSegments,
  parseTranscript,
  type TranscriptSegment,
} from '@/utils/transcript';

// Re-export the type for backward compatibility
export type { TranscriptSegment };

const EMPTY_TRANSCRIPT: TranscriptSegment[] = [];

interface CaseRecordContext {
  organizationId: string;
  programId: string;
  caseRecordId: string;
}

// Hook configuration interface
interface UseTranscriptOptions {
  pollInterval?: number; // Polling interval in ms (default: 2000)
  callStatus?: CallStatus; // Call status to determine if polling should continue
  callSid?: string; // Twilio Call SID for routing decision
  contextOverride?: OrgProgramContextOverride;
}

// Final call states where polling should stop
const FINAL_CALL_STATES = [
  CallStatus.CALL_STATUS_COMPLETED,
  CallStatus.CALL_STATUS_FAILED,
  CallStatus.CALL_STATUS_CANCELLED,
];

// Helper to check if polling should be active
function shouldEnablePolling(callStatus?: CallStatus): boolean {
  return !!callStatus && !FINAL_CALL_STATES.includes(callStatus);
}

// Helper to build context from store values
function buildContext(
  caseRecordId: string | undefined,
  selectedOrganizationId: string | null,
  selectedProgramId: string | null,
): CaseRecordContext | null {
  if (!caseRecordId || !selectedOrganizationId || !selectedProgramId) return null;
  return {
    organizationId: selectedOrganizationId,
    programId: selectedProgramId,
    caseRecordId,
  };
}

// Helper to create incremental transcript fetcher
function createIncrementalFetcher(
  context: CaseRecordContext | null,
  assetId: string | undefined,
  transcriptOffsetRef: React.MutableRefObject<number>,
  existingSegmentsRef: React.MutableRefObject<TranscriptSegment[]>,
) {
  return async (): Promise<TranscriptSegment[]> => {
    if (!assetId || !context) return existingSegmentsRef.current;

    const offset = transcriptOffsetRef.current || 0;
    const response = await assetsService.downloadAssetWithRange(context, assetId, offset);
    const text = response.data;

    if (!text || text.trim().length === 0) return existingSegmentsRef.current;

    const newSegments = parseTranscript(text);
    const mergedSegments = mergeTranscriptSegments(existingSegmentsRef.current, newSegments);

    if (mergedSegments.length > existingSegmentsRef.current.length) {
      transcriptOffsetRef.current += new TextEncoder().encode(text).length;
    }

    existingSegmentsRef.current = mergedSegments;
    return mergedSegments;
  };
}

// Helper to create full transcript fetcher
function createFullTranscriptFetcher(
  context: CaseRecordContext | null,
  assetId: string | undefined,
  callSid: string | undefined,
) {
  return async (): Promise<TranscriptSegment[]> => {
    if (!assetId || !context) return EMPTY_TRANSCRIPT;

    const blob = callSid
      ? await assetsService.downloadAsset(context, assetId, 'transcript')
      : await assetsService.downloadAssetFromArcus(context, assetId, 'transcript');

    const text = await blob.text();
    return parseTranscript(text);
  };
}

// eslint-disable-next-line complexity
export function useTranscript(
  caseRecordId: string | undefined,
  assetId: string | undefined,
  options: UseTranscriptOptions = {},
) {
  const { pollInterval = 2000, callStatus, callSid, contextOverride } = options;
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const effectiveOrganizationId = contextOverride?.organizationId || selectedOrganizationId;
  const effectiveProgramId = contextOverride?.programId || selectedProgramId;

  const context = useMemo(
    () => buildContext(caseRecordId, effectiveOrganizationId, effectiveProgramId),
    [caseRecordId, effectiveOrganizationId, effectiveProgramId],
  );

  const shouldPoll = shouldEnablePolling(callStatus);
  const transcriptOffsetRef = useRef(0);
  const existingSegmentsRef = useRef<TranscriptSegment[]>(EMPTY_TRANSCRIPT);

  // Reset refs when assetId changes (switching between calls)
  useEffect(() => {
    transcriptOffsetRef.current = 0;
    existingSegmentsRef.current = EMPTY_TRANSCRIPT;
  }, [assetId]);

  const incrementalQuery = useQuery({
    queryKey: [
      'transcript-incremental',
      context?.organizationId,
      context?.programId,
      caseRecordId,
      assetId,
    ],
    queryFn: createIncrementalFetcher(context, assetId, transcriptOffsetRef, existingSegmentsRef),
    enabled: shouldPoll && !!assetId && !!context,
    refetchInterval: pollInterval,
    refetchIntervalInBackground: true,
    staleTime: 0,
    retry: failureCount => failureCount < 3,
  });

  const fullQuery = useQuery({
    // 'no-sid' prevents undefined from collapsing the cache key — TanStack Query
    // treats undefined as absent, so callSid undefined→real SID would not
    // trigger a re-fetch and the wrong-endpoint result would be cached forever.
    queryKey: [
      'transcript',
      context?.organizationId,
      context?.programId,
      caseRecordId,
      assetId,
      callSid ?? 'no-sid',
    ],
    queryFn: createFullTranscriptFetcher(context, assetId, callSid),
    enabled: !!assetId && !!context && !shouldPoll,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  return {
    transcript: shouldPoll
      ? (incrementalQuery.data ?? EMPTY_TRANSCRIPT)
      : (fullQuery.data ?? EMPTY_TRANSCRIPT),
    isLoading: fullQuery.isLoading || incrementalQuery.isLoading,
    error: fullQuery.error || incrementalQuery.error,
    hasTranscriptAsset: !!assetId,
  };
}
